import React, { useCallback, useMemo, useState, useContext, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowUp,
  faFolderOpen,
  faPlus,
  faArrowDown,
  faUpDownLeftRight,
  faCopy,
  faTrash,
  faHeart as farHeart,
  faUndo,
  faCheckDouble,
  faSquareCheck,
  faBook,
} from "@fortawesome/free-solid-svg-icons";

import service from "../utils/request";
import "animate.css";
import message from "../utils/message";
import NewFileModal from "./NewFileModal";
import MoveModal from "./MoveModal";
import Dialog from "./Dialog";
import UploadModal from "./UploadModal";
import Sort from "./Sort";
import Search from "./Search";
import { SpaceIcon } from "./Sidebar";
import { SpaceContext } from "../SpaceContext";
import { getAllowedActions } from "../utils/permissions";
import CheckInModal from './common/CheckInModal';
import CheckOutModal from './common/CheckOutModal';
import RestoreModal from './RestoreModal';


function triggerNativeDownload(url, filename) {
  // This function use native browser download function to download the file.
  const link = document.createElement("a");
  link.href = url;
  if (filename) {
    link.download = filename;
  }
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export default function Toolbar({
  spaces,
  path,
  setPath,
  fileList,
  setFileList,
  spaceInfo,
  getFileList,
  selectedItems,
  setSelectedItems,
  openFolder,
  sortOrder,
  setSortOrder,
  upload,
  view,
  setView,
  checkExists,
  resolvedPermissions,
  isTrashTab = false,
  switchSpace,
  uploadFolder
}) {
  const [isNewFileVisible, setIsNewFileVisible] = useState(false);
  const [isMoveModalVisible, setIsMoveModalVisible] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [isUploadModalVisible, setIsUploadModalVisible] = useState(false);
  const [isRestoreModalVisible, setIsRestoreModalVisible] = useState(false);
  const [libraryModalMode, setLibraryModalMode] = useState(null);
  const [restorePreviewData, setRestorePreviewData] = useState(null);
  const [isSpaceDropdownVisible, setIsSpaceDropdownVisible] = useState(false);

  const { coolApps } = useContext(SpaceContext);
  const currentSpace = useMemo(() => {
    const id = path[0]?.id;
    return (
      spaces.find((space) => space.id === id) || { id: "", name: "", class: "" }
    );
  }, [path, spaces]);

  const deleteActions = useMemo(() => ({
    confirmDelete: async () => {
      try {
        if (isTrashTab || currentSpace.id === "trash") {
          // Permanent delete ONLY for trash tab/space
          message("loading", "Permanently deleting items...");
          setIsDeleteModalVisible(false);

          await service.post("/filer/permanent_delete", Object.keys(selectedItems));
          await getFileList(spaceInfo.current_id);
          setSelectedItems({});
          message("success", "Items permanently deleted");
        } else {
          message("loading", "Moving to trash...");
          setIsDeleteModalVisible(false);

          await service.post("/filer/delete", Object.keys(selectedItems));
          await getFileList(spaceInfo.current_id);
          setSelectedItems([]);
          message("success", "Files Moved To Trash Successfully");
        }
      } catch (error) {
        message("error", error);
      }
    },

    confirmUnfavourite: async () => {
      try {
        message("loading", "Removing from favourites...");
        setIsDeleteModalVisible(false);

        const unfavouritePromises = Object.keys(selectedItems).map(async (itemId) => {
          const item = selectedItems[itemId];
          return service.post("/filer/rename", {
            item_id: itemId,
            new_name: item.item_name,
            is_favour: false,
            file_class: item.file_class_id || 1,
            keywords: item.keywords || item.item_name,
          });
        });

        await Promise.all(unfavouritePromises);
        await getFileList(spaceInfo.current_id);
        setSelectedItems([]);
        message("success", "Removed from favourites successfully");
      } catch (error) {
        message("error", error);
      }
    },

    showDeleteModal: () => setIsDeleteModalVisible(true),

    canDelete: () => Object.keys(selectedItems).length > 0
  }), [selectedItems, spaceInfo, getFileList, setSelectedItems, isTrashTab, currentSpace.id]);

  async function confirmDelete() {
    return deleteActions.confirmDelete();
  }

  async function confirmUnfavourite() {
    return deleteActions.confirmUnfavourite();
  }

  async function handleNavClick(index) {
    if (index + 1 === path.length) return;
    getFileList(path[index].id);
    const pathSlice = path.slice(0, index + 1);
    setPath(pathSlice);
  }
  function handleMoveClick() {
    if (Object.keys(selectedItems).length === 0) {
      message("warn", "There is no item be selected");
      return;
    }
    setIsMoveModalVisible(true);
  }

  const ToolbarTitle = useCallback(() => {
    if (currentSpace.id === "search") {
      return <Search setFileList={setFileList} spaceId={spaceInfo.space_id} />;
    }
    if (currentSpace.class === "special") {
      return (
        <nav aria-label="breadcrumb">
          <ol className="flex flex-wrap items-center mb-1 text-base list-none rounded-sm text-neutral-900">
            <li className="flex items-center cursor-pointer breadcrumb-item">
              <SpaceIcon space={currentSpace} />
              <span className="ms-3">{currentSpace.caption}</span>
            </li>
          </ol>
        </nav>
      );
    }
    return (
      path?.length > 0 && (
        <nav className="w-full" aria-label="breadcrumb">
          <ol className="flex items-center mb-1 overflow-x-auto text-base list-none rounded-sm flex-nowrap text-neutral-900">
            <li
              className="flex items-center cursor-pointer breadcrumb-item"
              onClick={() => handleNavClick(0)}
            >
              <SpaceIcon space={currentSpace} />
              <span className="ms-3">{path[0]?.name}</span>
            </li>
            {path.map((folder, index) => {
              return index > 0 ? (
                <li
                  key={folder.id}
                  className="flex whitespace-nowrap breadcrumb-item active"
                  aria-current="page"
                >
                  <span
                    className="cursor-pointer text-elliipsis"
                    onClick={() => handleNavClick(index)}
                  >
                    {folder.name}
                  </span>
                </li>
              ) : null;
            })}
          </ol>
        </nav>
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSpace, setFileList, path]);

  function ToolbarActionBtn({ icon, onClick, action }) {
    return (
      <button
        disabled={canPerformAction(action)}
        title={action}
        onClick={onClick}
        className="py-1 px-2.5 border border-gray-300 rounded-full w-fit h-fit text-neutral-900 hover:bg-gray-200 disabled:pointer-events-none disabled:border-gray-300/80 disabled:text-neutral-900/40 icon-right"
      >
        <FontAwesomeIcon icon={icon} size="sm" />
      </button>
    );
  }

  async function getRestorePreview() {
    if (Object.keys(selectedItems).length === 0) {
      message("warn", "No items selected for restore");
      return null;
    }

    try {
      const itemIds = Object.keys(selectedItems);
      const response = await service.post("/filer/restore_preview", itemIds);
      return response;
    } catch (error) {
      message("error", `Failed to get restore preview: ${error.message || error}`);
      return null;
    }
  }

  async function handleRestore() {
    if (Object.keys(selectedItems).length === 0) {
      message("warn", "No items selected for restore");
      return;
    }

    // Get restore preview first
    const previewData = await getRestorePreview();
    if (!previewData) return;

    // Show confirmation modal with paths
    setRestorePreviewData(previewData);
    setIsRestoreModalVisible(true);
  }

  async function confirmRestore() {
    try {
      message("loading", "Restoring items...");
      setIsRestoreModalVisible(false);

      const itemIds = Object.keys(selectedItems);
      const response = await service.post("/filer/restore", itemIds);

      await getFileList(spaceInfo.current_id);
      setSelectedItems({});

      if (response.restored_items && response.restored_items.length > 0) {
        const messages = response.restored_items.map(item =>
          `${item.item_name} restored to: ${item.restored_to}`
        );
        message("success", messages.join(", "));
      } else {
        message("success", "Items restored successfully");
      }
    } catch (error) {
      message("error", `Failed to restore items: ${error.message || error}`);
    }
  }

  function handleSelectAll() {
    const allSelected = Object.keys(selectedItems).length === fileList.length && fileList.length > 0;

    if (allSelected) {
      setSelectedItems({});
      message("info", "All items deselected");
    } else {
      const newSelectedItems = {};
      fileList.forEach(item => {
        const itemId = item.id || item.item_id || item.file_id || item.uuid;
        if (itemId) {
          newSelectedItems[itemId] = item;
        }
      });
      setSelectedItems(newSelectedItems);
      message("success", `${Object.keys(newSelectedItems).length} items selected`);
    }
  }

  function canPerformAction(action) {
    switch (action) {
      case "Create":
      case "Upload":
      case "Upload Folder":
        return false; // Always enable create and upload
      case "Copy":
        const selectedEntries = Object.entries(selectedItems);
        if (selectedEntries.length === 0) return true;

        const hasNonFolders = selectedEntries.some(([_, item]) => !item.is_folder);
        return !hasNonFolders;
      case "Check In":
        const checkInEntries = Object.entries(selectedItems);
        if (checkInEntries.length === 0) return true;
        const allValidCheckInFiles = checkInEntries.every(([_, item]) => !item.is_folder && !item.is_checkin);
        return !allValidCheckInFiles;
      case "Download":
        return Object.keys(selectedItems).length === 0;
      case "Select All":
      case "Deselect All":
        return fileList.length === 0;
      case "Remove from Favourites":
      case "Delete":
      case "Restore":
        return Object.keys(selectedItems).length === 0;
      case "Move":
        return Object.keys(selectedItems).length === 0;
      default:
        const allowedActions = getAllowedActions(resolvedPermissions) || [];
        return !allowedActions.includes(action);
    }
  }

  function handleCheckInClick() {
    const selectedEntries = Object.entries(selectedItems);
    if (selectedEntries.length === 0) {
      message("warn", "No files selected for check in");
      return;
    }
    const hasInvalid = selectedEntries.some(([_, item]) => item.is_folder || item.is_checkin);
    if (hasInvalid) {
      message("warn", "All selected items must be files and not already checked in");
      return;
    }
    setLibraryModalMode("checkin");
  }

  async function handleCopy() {
    if (Object.keys(selectedItems).length === 0) {
      message("warn", "No files selected for copy");
      return;
    }

    try {
      message("loading", "Copying files...");

      let copyCount = 0;
      let skippedFolders = 0;
      const selectedEntries = Object.entries(selectedItems);

      for (const [itemId, item] of selectedEntries) {
        if (item.is_folder) {
          skippedFolders++;
          console.log(`Skipping folder: ${item.item_name}`);
          continue;
        }

        try {
          const response = await service.post("/filer/copy", { item_id: itemId });
          if (response) {
            copyCount++;
          }
        } catch (copyError) {
          console.error(`Copy error for ${item.item_name}:`, copyError);
          message("error", `Failed to copy ${item.item_name}: ${copyError.message || copyError}`);
        }
      }

      if (copyCount > 0) {
        const itemText = copyCount === 1 ? 'file' : 'files';
        message("success", `${copyCount} ${itemText} copied successfully`);

        await getFileList(spaceInfo.current_id);
      }

      if (skippedFolders > 0) {
        const folderText = skippedFolders === 1 ? 'folder' : 'folders';
        message("warn", `${skippedFolders} ${folderText} skipped (folders cannot be copied)`);
      }

      if (copyCount === 0 && skippedFolders === 0) {
        message("warn", "No items were copied");
      }

    } catch (error) {
      message("error", "Copy operation failed");
    }
  }

  function ToolbarTools() {
    const allSelected = Object.keys(selectedItems).length === fileList.length && fileList.length > 0;
    const selectAllAction = allSelected ? "Deselect All" : "Select All";
    const selectAllIcon = allSelected ? faCheckDouble : faSquareCheck;

    if (
      currentSpace.id === "search" ||
      currentSpace.id === "recent" ||
      currentSpace.id === "tasks"
    ) {
      return "";
    }
    if (currentSpace.id === "trash" || isTrashTab) {
      return (
        <>
          <ToolbarActionBtn
            action={selectAllAction}
            icon={selectAllIcon}
            onClick={handleSelectAll}
          />
          <ToolbarActionBtn
            action={"Restore"}
            icon={faUndo}
            onClick={() => handleRestore()}
          />
          <ToolbarActionBtn
            action={"Delete"}
            icon={faTrash}
            onClick={() => setIsDeleteModalVisible(true)}
          />
        </>
      );
    }
    if (currentSpace.id === "favour") {
      return (
        <>
          <ToolbarActionBtn
            action={selectAllAction}
            icon={selectAllIcon}
            onClick={handleSelectAll}
          />
          <ToolbarActionBtn
            action={"Remove from Favourites"}
            icon={farHeart}
            onClick={() => setIsDeleteModalVisible(true)}
          />
        </>
      );
    }
    return (
      <>
        <ToolbarActionBtn
          action={selectAllAction}
          icon={selectAllIcon}
          onClick={handleSelectAll}
        />
        <ToolbarActionBtn
          action={"Upload"}
          icon={faArrowUp}
          onClick={() => setIsUploadModalVisible(true)}
        />
        <ToolbarActionBtn
          action={"Create"}
          icon={faPlus}
          onClick={() => setIsNewFileVisible(true)}
        />
        <ToolbarActionBtn
          action={"Check In"}
          icon={faBook}
          onClick={() => handleCheckInClick()}
        />
        <ToolbarActionBtn
          action={"Download"}
          icon={faArrowDown}
          onClick={() => handleDirectDownload()}
        />
        <ToolbarActionBtn
          action={"Move"}
          icon={faUpDownLeftRight}
          onClick={() => handleMoveClick()}
        />
        <ToolbarActionBtn
          action={"Copy"}
          icon={faCopy}
          onClick={() => handleCopy()}
        />
        <ToolbarActionBtn
          action={"Delete"}
          icon={faTrash}
          onClick={() => setIsDeleteModalVisible(true)}
        />
      </>
    );
  }

  function handleViewChange(view) {
    localStorage.setItem("view", view);
    setView(view);
  }

  async function handleDirectDownload() {
    if (Object.keys(selectedItems).length === 0) {
      message("warn", "No files selected for download");
      return;
    }

    try {
      message("loading", "Starting download...");

      const selectedEntries = Object.entries(selectedItems);
      let downloadCount = 0;

      for (const [itemId, item] of selectedEntries) {
        try {
          let downloadUrl;
          const encodedItemId = encodeURIComponent(itemId);

          if (spaceInfo.space_id && spaceInfo.space_id !== spaceInfo.current_id) {
            downloadUrl = `/filer/download/${encodedItemId}?space_id=${spaceInfo.space_id}`;
          } else {
            downloadUrl = `/filer/download/${encodedItemId}`;
          }

          const ext = (item.item_type || '').replace(/^\./, '');
          let filename = item.is_folder
            ? `${item.item_name}.zip`
            : ext ? `${item.item_name}.${ext}` : item.item_name;

          triggerNativeDownload(downloadUrl, filename);

          downloadCount++;
        } catch (downloadError) {
          message("error", `Failed to download ${item.item_name}: ${downloadError.message}`);
        }

        // Add longer delay for folders as ZIP creation takes more time
        const delay = item.is_folder ? 1000 : 300;
        await new Promise(resolve => setTimeout(resolve, delay));
      }

      if (downloadCount > 0) {
        const itemText = downloadCount === 1 ? 'item' : 'items';
        message("success", `${downloadCount} ${itemText} downloaded successfully`);
      } else {
        message("warn", "No items downloaded");
      }

    } catch (error) {
      message("error", "Download failed");
    }
  }

  // Close space dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isSpaceDropdownVisible && !event.target.closest('.relative')) {
        setIsSpaceDropdownVisible(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isSpaceDropdownVisible]);

  return (
    <div
      className="fixed pt-4 bg-gray-100 top-[37px] z-[1] w-[95%] sm:w-calc-100-minus-16rem"
    >
      {/* Title/Breadcrumb Section */}
      <div className="flex pb-1 w-full">
        <ToolbarTitle />
      </div>

      {/* Space Info Display with Dropdown - New section for mobile */}
      {spaceInfo.current_id !== "explorer" && (
        <div className="flex items-center pb-2 sm:hidden relative">
          <div
            className="flex items-center text-sm text-gray-600 bg-white px-3 py-1 rounded border cursor-pointer hover:bg-gray-50 transition-colors"
            onClick={() => setIsSpaceDropdownVisible(!isSpaceDropdownVisible)}
          >
            <SpaceIcon space={currentSpace} />
            <span className="ml-2 font-medium">{currentSpace.caption}</span>
            {path?.length > 1 && (
              <>
                <span className="mx-2 text-gray-400">/</span>
                <span className="text-gray-800">{path[path.length - 1]?.name}</span>
              </>
            )}
            <svg
              className={`ml-2 w-4 h-4 transition-transform ${isSpaceDropdownVisible ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>

          {/* Dropdown Menu */}
          {isSpaceDropdownVisible && (
            <div className="absolute top-full left-0 mt-1 w-64 bg-white border rounded-lg shadow-lg z-[1001] max-h-64 overflow-y-auto">
              <div className="py-1">
                {spaces.filter(space => space.id !== "explorer").map((space) => (
                  <div
                    key={space.id}
                    className={`flex items-center px-3 py-2 text-sm cursor-pointer hover:bg-gray-100 ${currentSpace.id === space.id ? 'bg-orange-50 text-orange-600' : 'text-gray-700'
                      }`}
                    onClick={() => {
                      switchSpace(space);
                      setIsSpaceDropdownVisible(false);
                    }}
                  >
                    <SpaceIcon space={space} />
                    <span className="ml-2">{space.caption}</span>
                    {currentSpace.id === space.id && (
                      <svg className="ml-auto w-4 h-4 text-orange-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {spaceInfo.current_id !== "explorer" && (
        <div className="flex flex-col sm:flex-row sm:justify-between pb-1 gap-2">
          {/* First row: Sort and View controls */}
          <div className="flex justify-between items-center w-full sm:w-auto">
            <div className="flex gap-2">
              <Sort sortOrder={sortOrder} setSortOrder={setSortOrder} />
            </div>
            <div className="flex items-center gap-2">
              <div className="w-20 h-8 border-[#ffa270] border-2 flex rounded">
                <div
                  onClick={() => handleViewChange("list")}
                  className={`flex-1 flex justify-center items-center cursor-pointer ${view === "list" && "bg-[#ffa270]"
                    }`}
                >
                  <svg
                    t="1725591142514"
                    className={`w-4 h-4 ${view === "list" ? "fill-white" : " fill-[#ffa270]"
                      }`}
                    viewBox="0 0 1024 1024"
                    version="1.1"
                    xmlns="http://www.w3.org/2000/svg"
                    p-id="10434"
                  >
                    <path
                      d="M892.928 128q28.672 0 48.64 19.968t19.968 48.64l0 52.224q0 28.672-19.968 48.64t-48.64 19.968l-759.808 0q-28.672 0-48.64-19.968t-19.968-48.64l0-52.224q0-28.672 19.968-48.64t48.64-19.968l759.808 0zM892.928 448.512q28.672 0 48.64 19.968t19.968 48.64l0 52.224q0 28.672-19.968 48.64t-48.64 19.968l-759.808 0q-28.672 0-48.64-19.968t-19.968-48.64l0-52.224q0-28.672 19.968-48.64t48.64-19.968l759.808 0zM892.928 769.024q28.672 0 48.64 19.968t19.968 48.64l0 52.224q0 28.672-19.968 48.64t-48.64 19.968l-759.808 0q-28.672 0-48.64-19.968t-19.968-48.64l0-52.224q0-28.672 19.968-48.64t48.64-19.968l759.808 0z"
                      p-id="10435"
                    ></path>
                  </svg>
                </div>
                <div
                  onClick={() => handleViewChange("grid")}
                  className={`flex-1 flex justify-center items-center cursor-pointer ${view === "grid" && "bg-[#ffa270]"
                    }`}
                >
                  <svg
                    t="1725527761116"
                    className={`w-4 h-4 ${view === "grid" ? "fill-white" : " fill-[#ffa270]"
                      }`}
                    viewBox="0 0 1024 1024"
                    version="1.1"
                    xmlns="http://www.w3.org/2000/svg"
                    p-id="8553"
                  >
                    <path
                      d="M64 160a96 96 0 0 1 96-96h224a96 96 0 0 1 96 96v224a96 96 0 0 1-96 96H160a96 96 0 0 1-96-96V160z m0 480a96 96 0 0 1 96-96h224a96 96 0 0 1 96 96v224a96 96 0 0 1-96 96H160a96 96 0 0 1-96-96V640z m576-96a96 96 0 0 0-96 96v224a96 96 0 0 0 96 96h224a96 96 0 0 0 96-96V640a96 96 0 0 0-96-96H640zM640 64h224q96 0 96 96v224q0 96-96 96H640q-96 0-96-96V160q0-96 96-96z"
                      p-id="8554"
                    ></path>

                  </svg>
                </div>
              </div>
            </div>
          </div>

          {/* Second row: Toolbar actions */}
          <div className="flex items-center justify-center sm:justify-end w-full sm:w-auto overflow-x-auto">
            <div className="flex items-center gap-1 sm:gap-2 min-w-fit px-2 sm:px-0">
              <ToolbarTools />
            </div>
          </div>
        </div>
      )}

      {isNewFileVisible && (
        <NewFileModal
          getFileList={getFileList}
          spaceInfo={spaceInfo}
          fileList={fileList}
          setIsNewFileVisible={setIsNewFileVisible}
          openFolder={openFolder}
        />
      )}
      {isMoveModalVisible && (
        <MoveModal
          getFileList={getFileList}
          setPath={setPath}
          openFolder={openFolder}
          spaces={spaces}
          selectedItems={selectedItems}
          setIsMoveModalVisible={setIsMoveModalVisible}
          fileList={fileList}
          spaceInfo={spaceInfo}
          path={path}
        />
      )}
      {isDeleteModalVisible && (
        <Dialog
          title={isTrashTab || currentSpace.id === "trash" ?
            "Are you sure you want to permanently delete these items?" :
            (currentSpace.id === "favour" ?
              "Are you sure you want to remove these items from favourites?" :
              "Are you sure you want to move these items to Trash?"
            )
          }
          onConfirm={currentSpace.id === "favour" ? confirmUnfavourite : confirmDelete}
          onCancel={() => setIsDeleteModalVisible(false)}
          confirmText={isTrashTab || currentSpace.id === "trash" ? "Permanently Delete" : (currentSpace.id === "favour" ? "Remove" : "Delete")}
          cancelText="Cancel"
          confirmButtonColor={isTrashTab || currentSpace.id === "trash" ? "bg-red-600" : "bg-[#ffa270]"}
        >
          <div className="w-[100%] border-gray-300 border mx-auto max-h-28 mb-5 rounded flex flex-wrap gap-x-8 gap-y-2 py-2 px-3 overflow-x-auto">
            {Object.keys(selectedItems).map((key) => (
              <div
                key={key}
                className="flex items-center gap-1"
              >
                <span className="text-sm">
                  {selectedItems[key].item_name}
                </span>
              </div>
            ))}
          </div>
          {(isTrashTab || currentSpace.id === "trash") && (
            <p className="text-red-600 text-sm mt-2 font-semibold">
              ⚠️ This action cannot be undone. Items will be permanently deleted from the system.
            </p>
          )}
        </Dialog>
      )}
      {isUploadModalVisible && (
        <UploadModal
          setIsVisible={setIsUploadModalVisible}
          spaceInfo={spaceInfo}
          getFileList={getFileList}
          upload={upload}
          uploadFolder={uploadFolder}
          checkExists={checkExists}
        />
      )}
      {isRestoreModalVisible && (
        <RestoreModal
          isVisible={isRestoreModalVisible}
          onCancel={() => setIsRestoreModalVisible(false)}
          onConfirm={confirmRestore}
          previewData={restorePreviewData}
        />
      )}
      {libraryModalMode === "checkin" && (
        <CheckInModal
          isOpen={true}
          onClose={() => setLibraryModalMode(null)}
          items={Object.values(selectedItems)}
          setFileList={setFileList}
          onSuccess={(selectedPolicyId, successIds, updatedNames) => {
            const successSet = new Set(successIds || Object.keys(selectedItems));
            if (setFileList) {
              setFileList((prevFileList) =>
                prevFileList.map((it) =>
                  successSet.has(it.item_id)
                    ? {
                      ...it,
                      item_name: updatedNames?.[it.item_id],
                      is_checkin: true,
                      policy_id: selectedPolicyId,
                    }
                    : it
                )
              );
            }
            setSelectedItems((prev) => {
              const next = { ...prev };
              Object.keys(next).forEach((id) => {
                if (successSet.has(id)) {
                  next[id] = {
                    ...next[id],
                    item_name: updatedNames?.[id],
                    is_checkin: true,
                    policy_id: selectedPolicyId,
                  };
                }
              });
              return next;
            });

            setLibraryModalMode("checkout");
          }}
        />
      )}
      {libraryModalMode === "checkout" && (
        <CheckOutModal
          isOpen={true}
          onClose={() => {
            setLibraryModalMode(null);
            setSelectedItems({});
          }}
          items={Object.values(selectedItems)}
          setFileList={setFileList}
        />
      )}
    </div>
  );
}
